// pages/TeamPage.jsx
// Unified team management — replaces /dashboard/team (BrokerTeamManager)
// and /admin/users (AdminUserManager) with a single role-aware page.
//
// Owner:   Organisation tab (full hierarchy, role/status/password controls) + Invite + Clients
// Manager: My Team tab (their advisers, add/edit/remove) + Clients

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Edit2, Trash2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TeamInvitePanel from '../components/TeamInvitePanel';
import api from '../services/api';
import { useMobile } from '../hooks/useMobile';
import MemberProfilePanel from '../components/MemberProfilePanel';

const tok = () => localStorage.getItem('token');
const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';

export default function TeamPage() {
  const mobile   = useMobile();
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem('user') || '{}');

  const isOwner   = user.role === 'broker' && ['owner','admin'].includes(user.brokerLevel);
  const isManager = user.role === 'broker' && user.brokerLevel === 'manager';

  const [activeTab, setActiveTab] = useState('team');
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // ── Manager-level data (/api/broker/team) ────────────────────────────────
  const [teamMembers,  setTeamMembers]  = useState([]);
  const [loadingTeam,  setLoadingTeam]  = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [formType,     setFormType]     = useState(null);
  const [editingId,    setEditingId]    = useState(null);
  const [formData,     setFormData]     = useState({ name: '', email: '', password: '' });

  // ── Owner-level data (/api/admin/users) ──────────────────────────────────
  const [allUsers,     setAllUsers]     = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [resetModal,   setResetModal]   = useState(null);
  const [newPassword,  setNewPassword]  = useState('');
  const [resetSaving,  setResetSaving]  = useState(false);
  const [resetError,   setResetError]   = useState('');
  const [showPw,       setShowPw]       = useState(false);
  const [roleChanging, setRoleChanging] = useState(null);

  // ── Member profile panel ─────────────────────────────────────────────────
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  // ── Shared: clients ──────────────────────────────────────────────────────
  const [clients,      setClients]      = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsLoaded,  setClientsLoaded]  = useState(false);

  const flash = (setter, msg) => { setter(msg); setTimeout(() => setter(''), 4000); };

  const fetchTeam = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const res = await fetch(`${API}/broker/team`, { headers: { Authorization: `Bearer ${tok()}` } });
      if (res.ok) setTeamMembers(await res.json());
      else setError('Failed to load team');
    } catch { setError('Network error'); }
    finally { setLoadingTeam(false); }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    if (!isOwner) return;
    setLoadingUsers(true);
    try {
      const { data } = await api.get('/admin/users');
      setAllUsers(data.users);
    } catch { setError('Failed to load users'); }
    finally { setLoadingUsers(false); }
  }, [isOwner]);

  const fetchClients = useCallback(async () => {
    if (clientsLoaded) return;
    setLoadingClients(true);
    try {
      const res = await fetch(`${API}/broker/team/clients`, { headers: { Authorization: `Bearer ${tok()}` } });
      if (res.ok) { setClients(await res.json()); setClientsLoaded(true); }
    } catch {}
    finally { setLoadingClients(false); }
  }, [clientsLoaded]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);
  useEffect(() => { if (isOwner) fetchAllUsers(); }, [isOwner, fetchAllUsers]);
  useEffect(() => { if (activeTab === 'clients') fetchClients(); }, [activeTab, fetchClients]);

  // ── Owner actions ─────────────────────────────────────────────────────────
  const toggleStatus = async (u) => {
    const next = u.status === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/admin/users/${u.id}/status`, { status: next });
      flash(setSuccess, `${u.name} ${next === 'active' ? 'activated' : 'deactivated'}`);
      fetchAllUsers();
    } catch { flash(setError, 'Failed to update status'); }
  };

  const changeRole = async (u, broker_level) => {
    setRoleChanging(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/role`, { broker_level });
      flash(setSuccess, `${u.name} is now ${broker_level}`);
      fetchAllUsers();
    } catch { flash(setError, 'Failed to update role'); }
    finally { setRoleChanging(null); }
  };

  const assignManager = async (u, manager_id) => {
    try {
      await api.patch(`/admin/users/${u.id}/manager`, { manager_id: manager_id || null });
      flash(setSuccess, 'Manager assignment updated');
      fetchAllUsers();
    } catch { flash(setError, 'Failed to assign manager'); }
  };

  const openReset = (u) => { setResetModal(u); setNewPassword(''); setResetError(''); setShowPw(false); };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { setResetError('Minimum 8 characters'); return; }
    setResetSaving(true); setResetError('');
    try {
      await api.post(`/admin/users/${resetModal.id}/reset-password`, { new_password: newPassword });
      setResetModal(null);
      flash(setSuccess, `Password reset for ${resetModal.name}`);
    } catch (err) { setResetError(err.response?.data?.error || 'Failed to reset'); }
    finally { setResetSaving(false); }
  };

  // ── Manager actions ───────────────────────────────────────────────────────
  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    try {
      const url    = editingId ? `/api/broker/team/${editingId}` : '/api/broker/team';
      const method = editingId ? 'PATCH' : 'POST';
      const res    = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ ...formData, role: formType }),
      });
      if (res.ok) { fetchTeam(); resetForm(); }
      else {
        const d = await res.json().catch(() => ({}));
        setError(d.message || d.error || 'Failed to save');
      }
    } catch { setError('Network error'); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this team member? They will no longer be able to log in.')) return;
    try {
      await fetch(`/api/broker/team/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${tok()}` } });
      fetchTeam();
    } catch {}
  };

  const handleReactivate = async (member) => {
    if (!window.confirm(`Reactivate ${member.name}? They will be able to log in again.`)) return;
    try {
      const res = await fetch(`/api/broker/team/${member.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ status: 'active' }),
      });
      if (res.ok) fetchTeam();
      else flash(setError, 'Failed to reactivate');
    } catch { flash(setError, 'Network error'); }
  };

  const handleSendReset = async (member) => {
    if (!window.confirm(`Send a password reset link to ${member.email}?`)) return;
    try {
      const res  = await fetch('/api/auth/send-reset-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ user_id: member.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) alert(data.message || 'Reset link sent.');
      else setError(data.error || 'Failed to send');
    } catch {}
  };

  const resetForm = () => {
    setShowForm(false); setFormType(null); setEditingId(null);
    setFormData({ name: '', email: '', password: '' });
  };

  // ── Derived lists ─────────────────────────────────────────────────────────
  // 'admin' (added 20 Aug 2026, full access like owner but not a director —
  // see ROLES-PERMISSIONS-TIDYUP-SCOPE.md §5) gets its own org-chart section,
  // parallel to Owners, rather than being folded into it — they're
  // meaningfully different (billing/agreement authority is owner-only).
  const adminUsers    = allUsers.filter(u => u.broker_level === 'admin');
  const adminDirectReports = allUsers.filter(u =>
    u.manager_id && !['manager', 'owner', 'admin'].includes(u.broker_level) && adminUsers.some(a => a.id === u.manager_id));
  const managers      = teamMembers.filter(m => m.broker_level === 'manager');
  const advisers      = teamMembers.filter(m => m.broker_level === 'adviser');
  const allManagers   = allUsers.filter(u => ['manager', 'owner', 'admin'].includes(u.broker_level));
  const ownerUsers    = allUsers.filter(u => u.broker_level === 'owner');
  const ownerDirectReports = allUsers.filter(u =>
    u.manager_id && !['manager', 'owner', 'admin'].includes(u.broker_level) && ownerUsers.some(o => o.id === u.manager_id));
  const managersList  = allUsers.filter(u => u.broker_level === 'manager');
  const unassigned    = allUsers.filter(u => (u.broker_level || 'adviser') === 'adviser' && !u.manager_id);

  const activeTeamCount = teamMembers.filter(m => m.status !== 'inactive').length;
  const activeAllCount  = allUsers.filter(u => u.status !== 'inactive').length;
  const totalCount = isOwner ? activeAllCount : activeTeamCount;

  const tabs = [
    { key: 'team',    label: isOwner ? '🏢 Organisation' : '👔 My Team', count: totalCount },
    ...(isOwner ? [{ key: 'invite', label: '✉️ Invite' }] : []),
    { key: 'clients', label: '👤 Clients', count: clientsLoaded ? clients.length : null },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Sidebar />
      <div style={{
        marginLeft: mobile ? 0 : 70, paddingTop: mobile ? 64 : 0,
        padding: mobile ? '64px 16px 24px' : '32px 40px',
        minHeight: '100vh', background: '#f8f9fa',
      }}>
        <div style={{ maxWidth: 960 }}>

          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0e1b2c', margin: 0 }}>Team</h1>
            <p style={{ color: '#718096', marginTop: 4, fontSize: 14 }}>
              {isOwner
                ? `${activeAllCount} people in your organisation`
                : `${activeTeamCount} team member${activeTeamCount !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Alerts */}
          {error && (
            <div style={{ display: 'flex', gap: 10, padding: '12px 16px', background: '#fee2e2', borderRadius: 8, marginBottom: 16, color: '#991b1b', fontSize: 13, alignItems: 'center' }}>
              <AlertCircle size={16} /> {error}
              <button onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontSize: 18, lineHeight: 1 }}>✕</button>
            </div>
          )}
          {success && (
            <div style={{ padding: '12px 16px', background: '#d1fae5', borderRadius: 8, marginBottom: 16, color: '#065f46', fontSize: 13, fontWeight: 500, border: '1px solid #6ee7b7' }}>
              {success}
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '2px solid #EDF2F7', marginBottom: 28, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{
                  background: 'none', border: 'none', padding: '10px 14px', fontSize: 14,
                  cursor: 'pointer', fontWeight: activeTab === tab.key ? 700 : 500,
                  color: activeTab === tab.key ? '#0e1b2c' : '#718096',
                  borderBottom: activeTab === tab.key ? '3px solid #10b981' : '3px solid transparent',
                  marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6,
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                {tab.label}
                {tab.count != null && tab.count > 0 && (
                  <span style={{ background: activeTab === tab.key ? '#10b981' : '#a0aec0', color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10 }}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── TEAM TAB: Owner ── */}
          {activeTab === 'team' && isOwner && (
            loadingUsers ? <Spinner /> : <>
              {ownerUsers.length > 0 && (
                <OrgGroup
                  title="Owners"
                  desc="Full platform access"
                  count={ownerUsers.length + ownerDirectReports.length}>
                  <OrgTable
                    users={ownerUsers.flatMap(o => [o, ...ownerDirectReports.filter(u => u.manager_id === o.id)])}
                    managerIds={ownerUsers.map(o => o.id)}
                    allManagers={allManagers}
                    changeRole={changeRole} assignManager={assignManager}
                    toggleStatus={toggleStatus} openReset={openReset} roleChanging={roleChanging}
                    onSelect={u => setSelectedMemberId(u.id)} />
                </OrgGroup>
              )}
              {adminUsers.length > 0 && (
                <OrgGroup
                  title="Admins"
                  desc="Full access, not a company director (no billing/agreement authority)"
                  count={adminUsers.length + adminDirectReports.length}>
                  <OrgTable
                    users={adminUsers.flatMap(a => [a, ...adminDirectReports.filter(u => u.manager_id === a.id)])}
                    managerIds={adminUsers.map(a => a.id)}
                    allManagers={allManagers}
                    changeRole={changeRole} assignManager={assignManager}
                    toggleStatus={toggleStatus} openReset={openReset} roleChanging={roleChanging}
                    onSelect={u => setSelectedMemberId(u.id)} />
                </OrgGroup>
              )}
              {managersList.length > 0 && (
                <OrgGroup
                  title="Managers & Their Advisers"
                  desc="Advisers are indented beneath their manager"
                  count={managersList.length + allUsers.filter(u => (u.broker_level||'adviser') === 'adviser' && u.manager_id).length}>
                  <OrgTable
                    users={managersList.flatMap(mgr => [mgr, ...allUsers.filter(u => u.manager_id === mgr.id)])}
                    managerIds={managersList.map(m => m.id)}
                    allManagers={allManagers}
                    changeRole={changeRole} assignManager={assignManager}
                    toggleStatus={toggleStatus} openReset={openReset} roleChanging={roleChanging}
                    onSelect={u => setSelectedMemberId(u.id)} />
                </OrgGroup>
              )}
              {unassigned.length > 0 && (
                <OrgGroup title="Unassigned Advisers" desc="Not yet assigned to a manager" count={unassigned.length}>
                  <OrgTable users={unassigned} allManagers={allManagers} managerIds={[]}
                    changeRole={changeRole} assignManager={assignManager}
                    toggleStatus={toggleStatus} openReset={openReset} roleChanging={roleChanging}
                    onSelect={u => setSelectedMemberId(u.id)} />
                </OrgGroup>
              )}
              {allUsers.length === 0 && <EmptyState icon="👥" msg="No users found." />}
            </>
          )}

          {/* ── TEAM TAB: Manager ── */}
          {activeTab === 'team' && !isOwner && (
            loadingTeam ? <Spinner /> : <>
              <MemberSection title="Managers" members={managers}
                onSelect={m => setSelectedMemberId(m.id)}
                onDeactivate={handleDeactivate} onReactivate={handleReactivate} onReset={handleSendReset} />
              <MemberSection title="Advisers" members={advisers}
                onSelect={m => setSelectedMemberId(m.id)}
                onDeactivate={handleDeactivate} onReactivate={handleReactivate} onReset={handleSendReset} />
              <div style={{ marginTop: 8 }}>
                <TeamInvitePanel managers={managers} isManager={isManager} />
              </div>
            </>
          )}

          {/* ── INVITE TAB (owner) ── */}
          {activeTab === 'invite' && (
            <TeamInvitePanel
              managers={[
                { id: user.id, name: `${user.name} (you, ${user.brokerLevel === 'admin' ? 'admin' : 'owner'})` },
                ...(managersList.length > 0 ? managersList : managers),
              ]}
              isManager={false}
            />
          )}

          {/* ── CLIENTS TAB ── */}
          {activeTab === 'clients' && (
            loadingClients ? <Spinner /> :
            clients.length === 0
              ? <EmptyState icon="👤" msg="No client portal accounts yet." sub="Clients appear here once invited via a deal." />
              : <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
                  {clients.map(c => <ClientRow key={c.deal_id} client={c} navigate={navigate} />)}
                </div>
          )}

          {/* ── Add/Edit modal (manager) ── */}
          {showForm && (
            <Modal onClose={resetForm}>
              <h2 style={{ margin: '0 0 16px', fontSize: 18, color: '#0e1b2c' }}>
                {editingId ? 'Edit' : 'Add'} {formType === 'manager' ? 'Manager' : 'Adviser'}
              </h2>
              <form onSubmit={handleTeamSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Name',     key: 'name',     type: 'text' },
                  { label: 'Email',    key: 'email',    type: 'email' },
                  { label: editingId ? 'New password (leave blank to keep)' : 'Password', key: 'password', type: 'password' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 4 }}>{f.label}</label>
                    <input type={f.type} value={formData[f.key]}
                      onChange={e => setFormData({ ...formData, [f.key]: e.target.value })}
                      required={f.key !== 'password' || !editingId}
                      style={modalInput} />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button type="button" onClick={resetForm} style={modalBtnSecondary}>Cancel</button>
                  <button type="submit" style={modalBtnPrimary}>{editingId ? 'Save' : 'Create'}</button>
                </div>
              </form>
            </Modal>
          )}

          {/* ── Member profile panel ── */}
          {selectedMemberId && (
            <MemberProfilePanel
              memberId={selectedMemberId}
              allManagers={isOwner ? allManagers : managers}
              onClose={() => setSelectedMemberId(null)}
              onSaved={() => { fetchTeam(); if (isOwner) fetchAllUsers(); }}
              onResetPassword={u => { setSelectedMemberId(null); openReset(u); }}
            />
          )}

          {/* ── Reset password modal (owner) ── */}
          {resetModal && (
            <Modal onClose={() => setResetModal(null)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 18, color: '#0e1b2c' }}>Reset Password</h2>
                <button onClick={() => setResetModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#718096' }}>✕</button>
              </div>
              <p style={{ color: '#718096', fontSize: 13, margin: '0 0 16px' }}>
                Set a new password for <strong>{resetModal.name}</strong>. They'll be logged out of all sessions.
              </p>
              {resetError && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{resetError}</div>}
              <form onSubmit={handleReset}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 4 }}>New password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPw ? 'text' : 'password'} value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Minimum 8 characters" autoFocus style={modalInput} />
                    <button type="button" onClick={() => setShowPw(s => !s)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#718096' }}>
                      {showPw ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setResetModal(null)} style={modalBtnSecondary}>Cancel</button>
                  <button type="submit" disabled={resetSaving}
                    style={{ ...modalBtnPrimary, background: '#1e40af' }}>
                    {resetSaving ? 'Resetting…' : 'Reset Password'}
                  </button>
                </div>
              </form>
            </Modal>
          )}

        </div>
      </div>
    </>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Spinner() {
  return <div style={{ padding: 40, textAlign: 'center', color: '#718096', fontSize: 14 }}>Loading…</div>;
}

function EmptyState({ icon, msg, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>{icon}</div>
      <p style={{ margin: 0, fontSize: 15, color: '#4a5568' }}>{msg}</p>
      {sub && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#a0aec0' }}>{sub}</p>}
    </div>
  );
}

function Modal({ onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function OrgGroup({ title, desc, count, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0e1b2c' }}>{title}</h2>
          {desc && <p style={{ margin: '2px 0 0', fontSize: 12, color: '#a0aec0' }}>{desc}</p>}
        </div>
        <span style={{ background: '#EDF2F7', color: '#4a5568', fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 99 }}>{count}</span>
      </div>
      {children}
    </div>
  );
}

function OrgTable({ users, managerIds, allManagers, changeRole, assignManager, toggleStatus, openReset, roleChanging, onSelect }) {
  const [showInactive, setShowInactive] = React.useState(false);
  const activeUsers   = users.filter(u => u.status !== 'inactive');
  const inactiveUsers = users.filter(u => u.status === 'inactive');
  const visibleUsers  = showInactive ? users : activeUsers;

  const UserRow = ({ u }) => {
    const indented = managerIds.length > 0 && !managerIds.includes(u.id) && u.broker_level !== 'owner' && u.broker_level !== 'admin';
    const isInactive = u.status === 'inactive';
    return (
      <tr key={u.id} onClick={() => !isInactive && onSelect && onSelect(u)}
        style={{ borderBottom: '1px solid #F7FAFC', background: isInactive ? '#f9fafb' : '#fff',
          cursor: isInactive ? 'default' : 'pointer', opacity: isInactive ? 0.65 : 1 }}>
        <td style={{ padding: '12px 16px', paddingLeft: indented ? 36 : 16, verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {indented && <div style={{ width: 2, height: 20, background: '#E2E8F0', marginRight: 2, flexShrink: 0 }} />}
            <div style={{ width: 32, height: 32, borderRadius: '50%',
              background: isInactive ? '#e2e8f0' : '#0e1b2c',
              color: isInactive ? '#a0aec0' : '#10b981',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              {(u.name || '?')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: isInactive ? '#a0aec0' : '#0e1b2c', fontSize: 13 }}>{u.name}</div>
              <div style={{ fontSize: 11, color: '#a0aec0' }}>{u.email}</div>
            </div>
          </div>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <select value={u.broker_level || 'adviser'} disabled={roleChanging === u.id || isInactive}
            onChange={e => changeRole(u, e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #E2E8F0', borderRadius: 6, background: '#fff', cursor: isInactive ? 'default' : 'pointer' }}>
            {/* 'admin' added 20 Aug 2026 — full access like owner, not a
                director (no billing/agreement authority). See
                ROLES-PERMISSIONS-TIDYUP-SCOPE.md §5/§7. */}
            {['owner', 'admin', 'manager', 'adviser'].map(l => (
              <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
            ))}
          </select>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <select value={u.manager_id || ''} disabled={isInactive}
            onChange={e => assignManager(u, e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #E2E8F0', borderRadius: 6, background: '#fff', cursor: isInactive ? 'default' : 'pointer', maxWidth: 140 }}>
            <option value="">No manager</option>
            {allManagers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
            background: isInactive ? '#fee2e2' : '#d1fae5',
            color: isInactive ? '#991b1b' : '#065f46' }}>
            {u.status}
          </span>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {!isInactive && <Btn color="#e0f2fe" text="#075985" onClick={(e) => { e.stopPropagation(); openReset(u); }}>Reset PW</Btn>}
            <Btn
              color={isInactive ? '#d1fae5' : '#fee2e2'}
              text={isInactive ? '#065f46' : '#991b1b'}
              onClick={(e) => { e.stopPropagation(); toggleStatus(u); }}>
              {isInactive ? 'Activate' : 'Deactivate'}
            </Btn>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F7FAFC', borderBottom: '1px solid #EDF2F7' }}>
                {['User', 'Role', 'Manager', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#a0aec0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map(u => <UserRow key={u.id} u={u} />)}
              {activeUsers.length === 0 && !showInactive && (
                <tr><td colSpan={5} style={{ padding: '20px 16px', color: '#a0aec0', fontSize: 13 }}>No active users</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {inactiveUsers.length > 0 && (
        <button onClick={() => setShowInactive(v => !v)}
          style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#718096',
            fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', fontWeight: 500 }}>
          <span style={{ fontSize: 11, display: 'inline-block', transition: 'transform 0.15s',
            transform: showInactive ? 'rotate(90deg)' : 'none' }}>▶</span>
          {showInactive ? 'Hide' : 'Show'} deactivated
          <span style={{ background: '#e2e8f0', color: '#4a5568', fontSize: 11, fontWeight: 700,
            padding: '1px 7px', borderRadius: 10 }}>{inactiveUsers.length}</span>
        </button>
      )}
    </div>
  );
}

function MemberSection({ title, members, onSelect, onDeactivate, onReactivate, onReset }) {
  const [showInactive, setShowInactive] = React.useState(false);
  const active   = members.filter(m => m.status !== 'inactive');
  const inactive = members.filter(m => m.status === 'inactive');

  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0e1b2c', margin: '0 0 12px' }}>
        {title} <span style={{ fontSize: 13, fontWeight: 400, color: '#a0aec0' }}>({active.length})</span>
      </h2>
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
        {active.length === 0
          ? <div style={{ padding: '20px 16px', color: '#a0aec0', fontSize: 13 }}>No active {title.toLowerCase()} yet</div>
          : active.map(m => (
            <div key={m.id} onClick={() => onSelect && onSelect(m)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F7FAFC', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#0e1b2c', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                  {(m.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#0e1b2c', fontSize: 14 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: '#718096' }}>{m.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Btn color="#e0f2fe" text="#075985" onClick={(e) => { e.stopPropagation(); onReset(m); }}>🔑 Reset</Btn>
                <Btn color="#fee2e2" text="#991b1b" onClick={(e) => { e.stopPropagation(); onDeactivate(m.id); }}>Deactivate</Btn>
              </div>
            </div>
          ))
        }
      </div>

      {inactive.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowInactive(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096', fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', fontWeight: 500 }}>
            <span style={{ fontSize: 11, display: 'inline-block', transition: 'transform 0.15s',
              transform: showInactive ? 'rotate(90deg)' : 'none' }}>▶</span>
            {showInactive ? 'Hide' : 'Show'} deactivated
            <span style={{ background: '#e2e8f0', color: '#4a5568', fontSize: 11, fontWeight: 700,
              padding: '1px 7px', borderRadius: 10 }}>{inactive.length}</span>
          </button>
          {showInactive && (
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginTop: 6 }}>
              {inactive.map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', borderBottom: '1px solid #F7FAFC', opacity: 0.75 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e2e8f0', color: '#a0aec0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                      {(m.name || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#4a5568', fontSize: 14 }}>{m.name}</div>
                      <div style={{ fontSize: 12, color: '#a0aec0' }}>{m.email} · Deactivated</div>
                    </div>
                  </div>
                  <Btn color="#d1fae5" text="#065f46" onClick={() => onReactivate(m)}>Reactivate</Btn>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ClientRow({ client, navigate }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F7FAFC' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
          {(client.client_name || '?')[0].toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 600, color: '#0e1b2c', fontSize: 14 }}>{client.client_name}</div>
          <div style={{ fontSize: 12, color: '#718096' }}>{client.client_email}</div>
          {client.broker_name && <div style={{ fontSize: 11, color: '#a0aec0' }}>Broker: {client.broker_name}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {client.deal_ref && <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', background: '#d1fae5', padding: '2px 7px', borderRadius: 99 }}>{client.deal_ref}</span>}
        {client.deal_stage_name && <span style={{ fontSize: 11, fontWeight: 600, color: '#4a5568', background: '#EDF2F7', padding: '2px 7px', borderRadius: 99 }}>{client.deal_stage_name}</span>}
        <Btn color="#EDF2F7" text="#374151" onClick={() => navigate(`/deals/${client.deal_id}`)}>View Deal →</Btn>
      </div>
    </div>
  );
}

function Btn({ color, text, onClick, children }) {
  return (
    <button onClick={onClick} style={{ padding: '5px 10px', background: color, color: text, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center' }}>
      {children}
    </button>
  );
}

const modalInput        = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, outline: 'none' };
const modalBtnSecondary = { flex: 1, padding: '10px', background: '#F7FAFC', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 };
const modalBtnPrimary   = { flex: 1, padding: '10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 };
